import { useMemo, useState } from 'react';
import { Code2, Search } from 'lucide-react';

import {
  API_METHODS,
  API_SURFACES,
  type ApiAccess,
  type ApiMethod,
  type ApiSurface,
} from '@tasks/contracts';

import { Badge, type BadgeTone } from '../../components/Badge';
import { EmptyState } from '../../components/EmptyState';
import { Input } from '../../components/Input';
import { Page, PageHeader } from '../../components/Page';
import { Table, type TableColumn } from '../../components/Table';
import { Tabs, type TabItem } from '../../components/Tabs';
import styles from './AdminPages.module.css';

/** Подписи уровней доступа: ровно то, что проверяет сервер (docs/03 §5). */
const ACCESS_LABELS: Readonly<Record<ApiAccess['kind'], string>> = {
  'system-admin': 'Системный админ',
  'project-admin': 'Админ проекта',
  member: 'Любой участник',
  token: 'Токен интеграции',
};

const ACCESS_TONES: Readonly<Record<ApiAccess['kind'], BadgeTone>> = {
  'system-admin': 'danger',
  'project-admin': 'warning',
  member: 'neutral',
  token: 'primary',
};

/** Скоуп показываем рядом с ролью: для токена это и есть его права. */
function accessLabel(access: ApiAccess): string {
  if (access.kind === 'token' && access.scope !== undefined) {
    return `${ACCESS_LABELS.token} · ${access.scope}`;
  }
  return ACCESS_LABELS[access.kind];
}

type SurfaceFilter = ApiSurface | 'all';

const TABS: readonly TabItem<SurfaceFilter>[] = [
  { value: 'all', label: 'Все', count: API_METHODS.length },
  ...API_SURFACES.map((surface) => ({
    value: surface.id as SurfaceFilter,
    label: surface.title,
    count: API_METHODS.filter((entry) => entry.surface === surface.id).length,
  })),
];

const COLUMNS: readonly TableColumn<ApiMethod>[] = [
  {
    id: 'method',
    header: 'Метод',
    width: '90px',
    render: (entry) => <Badge tone="accent">{entry.method}</Badge>,
  },
  {
    id: 'path',
    header: 'Путь',
    width: '360px',
    render: (entry) => <code className={styles['endpoint']}>{entry.path}</code>,
  },
  {
    id: 'purpose',
    header: 'Назначение',
    render: (entry) => entry.purpose,
  },
  {
    id: 'access',
    header: 'Кому доступно',
    width: '250px',
    render: (entry) => (
      <Badge tone={ACCESS_TONES[entry.access.kind]}>{accessLabel(entry.access)}</Badge>
    ),
  },
];

/** `/admin/api` — справочник методов API для администратора (docs/06 §3.7). */
export function ApiPage() {
  const [surface, setSurface] = useState<SurfaceFilter>('all');
  const [query, setQuery] = useState('');

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return API_METHODS.filter((entry) => surface === 'all' || entry.surface === surface).filter(
      (entry) =>
        needle.length === 0 ||
        entry.path.toLowerCase().includes(needle) ||
        entry.purpose.toLowerCase().includes(needle) ||
        entry.method.toLowerCase() === needle,
    );
  }, [surface, query]);

  const activeSurface = API_SURFACES.find((info) => info.id === surface);

  return (
    <Page width="wide">
      <PageHeader title="Методы API" />

      <p className="u-muted">
        Справочник методов, доступных администратору: настройки проекта, администрирование системы и
        публичный REST API для интеграций. Полное описание форматов — в <code>docs/05-api.md</code>{' '}
        и <code>docs/12-public-api.md</code>.
      </p>

      <Tabs items={TABS} value={surface} onChange={setSurface} ariaLabel="Группы методов" />

      {activeSurface === undefined ? null : (
        <p className="u-muted">
          <strong>{activeSurface.auth}</strong> · {activeSurface.note}
        </p>
      )}

      <div className={styles['toolbar']}>
        <div className={styles['searchBox']}>
          <Input
            label="Поиск"
            value={query}
            placeholder="Путь, назначение или метод"
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          icon={<Search size={20} />}
          title="Ничего не найдено"
          description="Измените запрос или выберите другую группу методов."
        />
      ) : (
        <div className={styles['tableWrap']}>
          <Table
            ariaLabel="Методы API"
            columns={COLUMNS}
            rows={rows}
            getRowId={(entry) => `${entry.method} ${entry.path}`}
          />
        </div>
      )}

      <p className="u-muted">
        <Code2 size={14} aria-hidden /> Всего методов: {rows.length} из {API_METHODS.length}.
      </p>
    </Page>
  );
}
